import React, { useEffect, useRef, useState } from 'react';
import { Toast } from '../ui.jsx';
import { teacherRequestEmailOtp, teacherVerifyEmailOtp } from '../storage';
import TeacherSidebar from './TeacherSidebar';
import TeacherDash from './TeacherDash';
import TeacherAttendance from './TeacherAttendance';
import TeacherMarks from './TeacherMarks';
import TeacherTimetable from './TeacherTimetable';
import TeacherStudents from './TeacherStudents';
import TeacherMyAttendance from './TeacherMyAttendance';
import TeacherMessages from './TeacherMessages';
import TeacherProfile from './TeacherProfile';
import AcademicCalendar from '../admin/AcademicCalendar';
import Exams from '../admin/Exams';

const LOGO_SRC = process.env.PUBLIC_URL + '/logo.png';

function TeacherPortal({ db, save, teacher, activeSessionId, onLogout }) {
  const [page, setPage] = useState('tdash');
  const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' ? window.innerWidth <= 768 : false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [emailPrompt, setEmailPrompt] = useState(() => {
    // Only show if no email AND not already dismissed this session
    return !teacher.em && !sessionStorage.getItem('lkps_email_prompted_' + (teacher.tid || teacher.id));
  });
  const [promptEmail, setPromptEmail] = useState('');
  const [promptOtp, setPromptOtp] = useState('');
  const [promptStep, setPromptStep] = useState('input'); // 'input' | 'otp'
  const [promptSaving, setPromptSaving] = useState(false);
  const [promptErr, setPromptErr] = useState('');
  const [teacherEmail, setTeacherEmail] = useState(teacher.em || '');
  const profileRef = React.useRef(null);
  const name = (teacher.fn || '') + ' ' + (teacher.ln || '');
  const photo = (db.tphotos || {})[teacher.tid] || (db.tphotos || {})[teacher.id] ||
    (() => { const t = (db.teachers || []).find(t => (t.puser || t.username) === (teacher.puser || teacher.username)); return t ? (db.tphotos || {})[t.tid || t.id] : null; })() || null;
  const initials = name.trim().split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase() || 'T';

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  useEffect(() => { if (!isMobile) setMobileNavOpen(false); }, [isMobile]);
  useEffect(() => { if (isMobile) setMobileNavOpen(false); }, [page, isMobile]);
  useEffect(() => {
    const h = e => { if (profileRef.current && !profileRef.current.contains(e.target)) setProfileOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const pages = {
    tdash: <TeacherDash db={db} teacher={teacher} name={name} photo={photo} setPage={setPage} />,
    tatt: <TeacherAttendance db={db} save={save} />,
    texam: <Exams db={db} save={save} logo={LOGO_SRC} activeSessionId={activeSessionId} teacher={teacher} />,
    ttt: <TeacherTimetable db={db} teacher={teacher} name={name} />,
    tcal: <AcademicCalendar db={db} save={save} />,
    tstu: <TeacherStudents db={db} save={save} teacher={teacher} activeSessionId={activeSessionId} />,
    tmyatt: <TeacherMyAttendance teacher={teacher} activeSessionId={activeSessionId} />,
    tmsg: <TeacherMessages db={db} teacher={teacher} />,
    tprofile: <TeacherProfile teacher={teacher} db={db} teacherEmail={teacherEmail} setTeacherEmail={setTeacherEmail} activeSessionId={activeSessionId} />,
  };

  return (
    <div className="tapp-shell" style={{ display: 'flex', height: '100vh', background: '#f0f4ff', fontFamily: 'Inter,sans-serif' }}>
      {/* Email prompt modal */}
      {emailPrompt && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <div style={{ background: '#fff', borderRadius: 20, padding: 32, width: '100%', maxWidth: 400, boxShadow: '0 24px 60px rgba(0,0,0,0.2)' }}>
            <div style={{ width: 48, height: 48, borderRadius: 14, background: '#fef3c7', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
              <span className="material-symbols-outlined" style={{ fontSize: 24, color: '#d97706', fontVariationSettings: "'FILL' 1" }}>mail</span>
            </div>
            <div style={{ fontSize: 18, fontWeight: 800, color: '#1e293b', textAlign: 'center', marginBottom: 6 }}>Add Your Email</div>
            <div style={{ fontSize: 13, color: '#64748b', textAlign: 'center', lineHeight: 1.6, marginBottom: 20 }}>
              Register your email to enable password recovery. You can also do this later from your profile.
            </div>
            {promptErr && <div style={{ background: '#fef2f2', color: '#dc2626', borderRadius: 10, padding: '9px 12px', fontSize: 12, fontWeight: 600, marginBottom: 12, border: '1px solid #fecaca' }}>{promptErr}</div>}
            {promptStep === 'input' ? <>
              <input value={promptEmail} onChange={e => { setPromptEmail(e.target.value); setPromptErr(''); }}
                placeholder="your@email.com" type="email"
                style={{ width: '100%', padding: '12px 14px', borderRadius: 10, border: '1.5px solid #e2e8f0', fontSize: 13, outline: 'none', boxSizing: 'border-box', marginBottom: 12 }} />
              <button onClick={async () => {
                if (!promptEmail.trim() || !/\S+@\S+\.\S+/.test(promptEmail)) { setPromptErr('Enter a valid email'); return; }
                setPromptSaving(true); setPromptErr('');
                try { await teacherRequestEmailOtp(promptEmail.trim()); setPromptStep('otp'); }
                catch (e) { setPromptErr(e.message || 'Failed to send OTP'); }
                setPromptSaving(false);
              }} disabled={promptSaving}
                style={{ width: '100%', padding: '12px', borderRadius: 10, border: 'none', background: 'linear-gradient(135deg,#059669,#10b981)', color: '#fff', fontWeight: 700, fontSize: 13, cursor: 'pointer', marginBottom: 8 }}>
                {promptSaving ? 'Sending OTP…' : 'Send OTP to Verify'}
              </button>
            </> : <>
              <div style={{ fontSize: 12, color: '#0369a1', fontWeight: 600, marginBottom: 8 }}>OTP sent to <b>{promptEmail}</b></div>
              <input value={promptOtp} onChange={e => { setPromptOtp(e.target.value); setPromptErr(''); }}
                placeholder="Enter 6-digit OTP" maxLength={6}
                style={{ width: '100%', padding: '12px 14px', borderRadius: 10, border: '1.5px solid #e2e8f0', fontSize: 13, outline: 'none', boxSizing: 'border-box', marginBottom: 12 }} />
              <button onClick={async () => {
                if (!promptOtp) { setPromptErr('Enter the OTP'); return; }
                setPromptSaving(true); setPromptErr('');
                try {
                  await teacherVerifyEmailOtp(promptOtp);
                  // Persist into localStorage so email survives a page refresh
                  try {
                    const raw = localStorage.getItem('lkps_user');
                    if (raw) { const u = JSON.parse(raw); u.em = promptEmail.trim(); localStorage.setItem('lkps_user', JSON.stringify(u)); }
                  } catch (_) {}
                  setTeacherEmail(promptEmail.trim());
                  sessionStorage.setItem('lkps_email_prompted_' + (teacher.tid || teacher.id), '1');
                  setEmailPrompt(false);
                } catch (e) {
                  setPromptErr(e.message || 'Invalid OTP — please re-enter your email');
                  setPromptOtp(''); setPromptEmail(''); setPromptStep('input');
                }
                setPromptSaving(false);
              }} disabled={promptSaving}
                style={{ width: '100%', padding: '12px', borderRadius: 10, border: 'none', background: 'linear-gradient(135deg,#059669,#10b981)', color: '#fff', fontWeight: 700, fontSize: 13, cursor: 'pointer', marginBottom: 8 }}>
                {promptSaving ? 'Verifying…' : 'Verify & Save Email'}
              </button>
              <button onClick={() => { setPromptStep('input'); setPromptOtp(''); setPromptErr(''); }}
                style={{ width: '100%', padding: '8px', borderRadius: 10, border: 'none', background: 'transparent', color: '#64748b', fontSize: 12, cursor: 'pointer', marginBottom: 4 }}>
                Change email
              </button>
            </>}
            <button onClick={() => {
              sessionStorage.setItem('lkps_email_prompted_' + (teacher.tid || teacher.id), '1');
              setEmailPrompt(false);
            }}
              style={{ width: '100%', padding: '10px', borderRadius: 10, border: '1px solid #e2e8f0', background: 'transparent', color: '#94a3b8', fontWeight: 600, fontSize: 12, cursor: 'pointer' }}>
              Skip for now
            </button>
          </div>
        </div>
      )}
      <TeacherSidebar page={page} setPage={setPage} user={teacher} db={db} onLogout={onLogout}
        isMobile={isMobile} mobileOpen={mobileNavOpen} onMobileClose={() => setMobileNavOpen(false)} />
      {isMobile && mobileNavOpen && (
        <div onClick={() => setMobileNavOpen(false)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', zIndex: 1100 }} />
      )}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minWidth: 0 }}>
        {/* Header */}
        <header style={{
          height: 64, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '0 24px', background: '#fff', borderBottom: '1px solid #e8edf5',
          boxShadow: '0 2px 12px rgba(0,31,77,0.07)', zIndex: 100, position: 'relative'
        }}>
          {/* Left */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
            {isMobile && (
              <button onClick={() => setMobileNavOpen(v => !v)}
                style={{ border: 0, background: 'transparent', cursor: 'pointer', color: '#1960a3', padding: 4, display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                <span className="material-symbols-outlined" style={{ fontSize: 24 }}>menu</span>
              </button>
            )}
            <div onClick={() => setPage('tdash')} style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
              <img src={LOGO_SRC} alt="LKPS" style={{ width: 36, height: 36, objectFit: 'contain', flexShrink: 0 }} onError={e => e.target.style.display = 'none'} />
              {!isMobile && (
                <div>
                  <div style={{ fontSize: 13, fontWeight: 900, color: '#002045', fontFamily: 'Manrope,sans-serif', letterSpacing: '0.01em', lineHeight: 1.1 }}>LORD KRISHNA PUBLIC SCHOOL</div>
                  <div style={{ fontSize: 9, fontWeight: 700, color: '#059669', letterSpacing: '0.12em', textTransform: 'uppercase' }}>Teacher Portal</div>
                </div>
              )}
            </div>
            {!isMobile && <>
              <div style={{ width: 1, height: 28, background: 'linear-gradient(180deg,transparent,#c7d9f5,transparent)', margin: '0 4px' }} />
              <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1 }}>
                <span style={{ fontSize: 9, fontWeight: 800, color: '#059669', letterSpacing: '0.14em', textTransform: 'uppercase' }}>Session</span>
                <span style={{ fontSize: 12, fontWeight: 900, color: '#002045', fontFamily: 'Manrope,sans-serif' }}>{db.settings?.year || '2026-27'}</span>
              </div>
            </>}
          </div>
          {/* Right — profile popover */}
          <div ref={profileRef} style={{ position: 'relative' }}>
            <button onClick={() => setProfileOpen(o => !o)}
              style={{ display: 'flex', alignItems: 'center', gap: isMobile ? 0 : 8, background: 'transparent', border: 'none', cursor: 'pointer', padding: isMobile ? 0 : '4px 6px', borderRadius: 10, transition: 'background 150ms' }}
              onMouseEnter={e => e.currentTarget.style.background = '#f1f5f9'}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
              <div style={{ width: 34, height: 34, borderRadius: '50%', background: 'linear-gradient(135deg,#059669,#10b981)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 800, color: '#fff', overflow: 'hidden', flexShrink: 0, boxShadow: '0 2px 8px rgba(5,150,105,0.25)' }}>
                {photo ? <img src={photo} alt={name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : initials}
              </div>
              {!isMobile && <>
                <div style={{ textAlign: 'left' }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: '#1e293b', lineHeight: 1.3 }}>{name.trim() || 'Teacher'}</div>
                  <div style={{ fontSize: 10, color: '#94a3b8' }}>{teacher.desig || teacher.su || 'Faculty'}</div>
                </div>
                <span className="material-symbols-outlined" style={{ fontSize: 16, color: '#94a3b8', transition: 'transform 200ms', transform: profileOpen ? 'rotate(180deg)' : 'rotate(0deg)' }}>expand_more</span>
              </>}
            </button>
            {profileOpen && (
              <div style={{ position: 'absolute', top: 'calc(100% + 10px)', right: 0, width: 260, background: '#fff', borderRadius: 16, boxShadow: '0 8px 32px rgba(0,32,69,0.14)', border: '1px solid #e8edf5', zIndex: 999, overflow: 'hidden', animation: 'pp-pop-in 150ms ease' }}>
                <div style={{ padding: '20px 20px 16px', background: 'linear-gradient(135deg,#f0fdf4,#ecfdf5)', borderBottom: '1px solid #e8edf5' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                    <div style={{ width: 48, height: 48, borderRadius: '50%', background: 'linear-gradient(135deg,#059669,#10b981)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: 18, color: '#fff', flexShrink: 0, overflow: 'hidden' }}>
                      {photo ? <img src={photo} alt={name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : initials}
                    </div>
                    <div>
                      <div style={{ fontSize: 15, fontWeight: 800, color: '#1e293b', fontFamily: 'Manrope,sans-serif' }}>{name.trim() || 'Teacher'}</div>
                      <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>{teacher.desig || teacher.su || 'Faculty'}</div>
                      <span style={{ display: 'inline-block', marginTop: 5, fontSize: 10, fontWeight: 700, color: '#059669', background: '#dcfce7', padding: '2px 10px', borderRadius: 20 }}>Teacher</span>
                    </div>
                  </div>
                </div>
                <div style={{ padding: '12px 20px' }}>
                  {teacher.cls && <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: '1px solid #f1f5f9' }}>
                    <span className="material-symbols-outlined" style={{ fontSize: 16, color: '#94a3b8' }}>class</span>
                    <div><div style={{ fontSize: 10, color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Class Teacher</div>
                      <div style={{ fontSize: 13, color: '#1e293b', fontWeight: 600 }}>{teacher.cls}</div></div>
                  </div>}
                  {teacher.su && <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: '1px solid #f1f5f9' }}>
                    <span className="material-symbols-outlined" style={{ fontSize: 16, color: '#94a3b8' }}>menu_book</span>
                    <div><div style={{ fontSize: 10, color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Subject</div>
                      <div style={{ fontSize: 13, color: '#1e293b', fontWeight: 600 }}>{teacher.su}</div></div>
                  </div>}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0' }}>
                    <span className="material-symbols-outlined" style={{ fontSize: 16, color: '#94a3b8' }}>person</span>
                    <div><div style={{ fontSize: 10, color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em' }}>View Profile</div>
                      <button onClick={() => { setPage('tprofile'); setProfileOpen(false); }} style={{ fontSize: 13, color: '#059669', fontWeight: 600, background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>Open full profile →</button></div>
                  </div>
                </div>
                <div style={{ padding: '0 12px 12px' }}>
                  <button onClick={() => { setProfileOpen(false); onLogout(); }}
                    style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, width: '100%', padding: '10px', borderRadius: 10, border: 'none', background: 'linear-gradient(135deg,#ef4444,#dc2626)', color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer', boxShadow: '0 4px 12px rgba(239,68,68,0.25)', transition: 'opacity 150ms' }}
                    onMouseEnter={e => e.currentTarget.style.opacity = '0.9'}
                    onMouseLeave={e => e.currentTarget.style.opacity = '1'}>
                    <span className="material-symbols-outlined" style={{ fontSize: 16 }}>logout</span>Sign Out
                  </button>
                </div>
              </div>
            )}
          </div>
        </header>
        {/* Page content */}
        <main style={{ flex: 1, overflowY: 'auto', padding: '28px 32px', background: '#f0f4ff' }}>
          {pages[page] || pages.tdash}
        </main>
      </div>
      <Toast />
    </div>
  );
}



export default TeacherPortal;
